/**
 * The refresh credential lives ONLY in the HttpOnly `linkhr_refresh_*` cookie,
 * which the backend rotates in the same response that consumes it. Nothing on
 * the client stores it, so nothing on the client can hand back an old one.
 *
 * Earlier builds also kept the JSON-body copy in localStorage
 * (`linkhr:refreshToken:{audience}`). A single-use, rotating token needs
 * exactly one carrier, and that copy was a second one that drifted: the
 * backend reads the cookie first and the body only when no cookie arrives, so
 * the copy sat unused while the cookie moved on without it (an audience
 * exchange mints the new family into the cookie only; a rotation dropped for an
 * audience change, or cut off by a navigation, advances the cookie only) and
 * was then presented exactly when the cookie was gone — after a browser restart
 * ends a "keep me signed in"-unticked session. Presenting an already-rotated
 * token revokes its whole family, and nothing ever deleted the copy, so every
 * later boot presented it again.
 *
 * Which stores can hold a copy: `mobile` (Employee Web) got a body token from
 * every sign-in and refresh, and authenticator-app (TOTP) sign-in returned one
 * for every audience, tenant and platform alike, so `admin` and `platform`
 * copies exist too. Dropping the copy is a change for every app.
 *
 * Nothing here ever hands a stored token back out.
 */
import type { Audience } from "../types.js";

const STORED_AUDIENCES: readonly Audience[] = ["admin", "mobile", "platform", "accounting"];

function keyFor(aud: Audience): string {
  return `linkhr:refreshToken:${aud}`;
}

function remove(aud: Audience): void {
  try {
    window.localStorage.removeItem(keyFor(aud));
  } catch {
    // No storage (private mode, SSR, some test harnesses): nothing to delete.
  }
}

export const refreshTokenStore = {
  /**
   * Whether `aud` has a copy. The token itself is never returned — the boot
   * uses the answer only as a one-time reason to try the cookie (see
   * refreshOnLoad).
   */
  has(aud: Audience): boolean {
    try {
      return window.localStorage.getItem(keyFor(aud)) !== null;
    } catch {
      // No storage (private mode, SSR, some test harnesses): nothing stored.
      return false;
    }
  },

  /**
   * Delete `aud`'s copy only. The global login restores `admin` before
   * `mobile`, and deleting the `mobile` copy during the `admin` pass would take
   * the `mobile` restore's reason to try away.
   */
  remove,

  /** Delete every audience's copy (logout: nothing may outlive the session). */
  clear(): void {
    for (const aud of STORED_AUDIENCES) remove(aud);
  },
};
